import i18next from 'i18next';
import moment from 'moment';
import { FC } from 'react';

import { ScheduleI, getScheduleStartDate } from '~src/interfaces/schedule.interface';
import { ScheduleErrors } from '~src/modules/surveys/components/schedules/scheduleItem/templates/ScheduleTemplate';
import {
  ButtonMargin,
  ButtonSectionContainer,
  ErrorContainer,
  TemplateError,
} from '~src/modules/surveys/components/schedules/scheduleItem/templates/scheduleTemplate.sc';
import SecondaryButton from '~src/shared/buttons/basicButtons/SecondaryButton.sc';
import PrimaryButtonLoading from '~src/shared/buttons/loadingButtons/PrimaryButtonLoading';

interface OwnProps {
  t: i18next.TFunction;
  cancelChanges: () => void;
  showButtons: boolean;
  saveLoading: boolean;
  savedSchedule: ScheduleI;
  editedSchedule: ScheduleI;
  isAddSchedule: boolean;
  errors: ScheduleErrors;
  changeActivity(active: boolean): void;
}

interface BtnState {
  txt: string;
  isVisible?: boolean;
  disabled?: boolean;
  days?: number;
}

interface SubmitButtonsState {
  draftBtn: BtnState;
  activeBtn: BtnState;
}

const submitButtonState = ({
  savedSchedule,
  editedSchedule,
  showButtons,
  saveLoading,
  isAddSchedule,
}: OwnProps): SubmitButtonsState => {
  const draftBtn = {
    txt: 'x.surveys.schedules.update_draft_button',
    isVisible: showButtons,
    disabled: saveLoading && editedSchedule.active,
  };
  const activeBtn = {
    txt: 'x.button.save',
    isVisible: showButtons,
    disabled: saveLoading && !editedSchedule.active,
    days: 0,
  };
  const startDate = getScheduleStartDate(editedSchedule);

  if (savedSchedule.active) {
    draftBtn.txt = 'x.surveys.schedules.deactivate_button';
    draftBtn.isVisible = true;
    if (!startDate) {
      activeBtn.isVisible = false;
    }
  } else {
    const dateNow = new Date();
    const momentDate = startDate ? moment(startDate) : null;
    if (momentDate) {
      if (momentDate.isBefore(dateNow, 'days')) {
        activeBtn.txt = 'x.surveys.schedules.activate_button';
      } else if (momentDate.isSame(dateNow, 'day')) {
        activeBtn.txt = 'x.surveys.schedules.send_today_button';
      } else if (momentDate.isSame(moment(dateNow).add(1, 'day'), 'days')) {
        activeBtn.txt = 'x.surveys.schedules.send_tomorrow_button';
      } else {
        activeBtn.days = momentDate.diff(dateNow, 'days');
        activeBtn.txt = 'x.surveys.schedules.send_in_button';
      }
      activeBtn.isVisible = true;
    } else {
      activeBtn.isVisible = false;
    }
  }

  if (isAddSchedule) {
    draftBtn.txt = 'x.surveys.schedules.saveas_draft_button';
  }

  return { draftBtn, activeBtn };
};

const checkForError = (errors: ScheduleErrors): boolean =>
  Object.values(errors).some((value) => value === true);

const ButtonSection: FC<OwnProps> = (props: OwnProps) => {
  const { t, showButtons = false, saveLoading = false} = props;
  const { draftBtn, activeBtn } = submitButtonState(props);
  const onDraftBtnClick = () => {
    props.changeActivity(false);
  };
  const onActiveBtnClick = () => {
    props.changeActivity(true);
  };

  return (
    <>
      <ButtonSectionContainer>
        {showButtons && (
          <ButtonMargin>
            <SecondaryButton type="button" onClick={props.cancelChanges} disabled={saveLoading}>
              <span data-translation="x.button.cancel">{t('x.button.cancel')}</span>
            </SecondaryButton>
          </ButtonMargin>
        )}
        {draftBtn.isVisible && (
          <ButtonMargin>
            <PrimaryButtonLoading
              onClick={onDraftBtnClick}
              $buttonColor="lightblue"
              isLoading={saveLoading && !draftBtn.disabled}
              disabled={draftBtn.disabled}
            >
              <span data-translation={draftBtn.txt}>{t(draftBtn.txt)}</span>
            </PrimaryButtonLoading>
          </ButtonMargin>
        )}
        {activeBtn.isVisible && (
          <ButtonMargin>
            <PrimaryButtonLoading
              onClick={onActiveBtnClick}
              $buttonColor="green"
              isLoading={saveLoading && !activeBtn.disabled}
              disabled={activeBtn.disabled}
            >
              <span data-translation={activeBtn.txt}>{t(activeBtn.txt, { count: activeBtn.days })}</span>
            </PrimaryButtonLoading>
          </ButtonMargin>
        )}
      </ButtonSectionContainer>
      <ErrorContainer>
        {checkForError(props.errors) && (
          <TemplateError data-translation="x.surveys.schedules.template_error">
            {t('x.surveys.schedules.template_error')}
          </TemplateError>
        )}
      </ErrorContainer>
    </>
  );
};

export default ButtonSection;
